import { zodResolver } from '@hookform/resolvers/zod'
import { X } from 'lucide-react'
import { useEffect } from 'react'
import { useForm, Controller, type SubmitHandler } from 'react-hook-form'
import { z } from 'zod'

import { CurrencySelector, ImageUploader } from '@/components/common'
import { DraftSavedIndicator } from '@/components/common/draft-saved-indicator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'

import { useEnhancedDraft } from '@/hooks'
import {
  useUploadFile, type FileMetadata,
  useCreateReimbursement,
  type ReimbursementType,
  type CreateReimbursementInput,
} from '@/hooks'

interface ReimbursementFormProps {
  onClose: () => void
  onSuccess: () => void
}

interface ReimbursementDraftData extends CreateReimbursementInput {
  uploadedFiles: FileMetadata[]
}

const reimbursementTypes: { value: ReimbursementType; label: string }[] = [
  { value: 'travel', label: '差旅' },
  { value: 'office', label: '办公' },
  { value: 'meal', label: '餐饮' },
  { value: 'transport', label: '交通' },
  { value: 'other', label: '其他' },
]

const initialDraftData: ReimbursementDraftData = {
  type: 'office',
  amount: 0,
  currency: 'AED',
  description: '',
  uploadedFiles: [],
}

// Zod Schema Definition
const formSchema = z.object({
  type: z.enum(['travel', 'office', 'meal', 'transport', 'other'] as const, {
    required_error: '请选择报销类型',
  }),
  amount: z.coerce.number().min(0.01, '金额必须大于0'),
  currency: z.string().default('AED'),
  description: z.string().min(1, '请输入报销说明'),
  uploadedFiles: z.array(z.any()), // Assuming FileMetadata type for now, simplified for schema
})

type ReimbursementFormValues = z.infer<typeof formSchema>

/**
 * 报销表单组件
 * Refactored to use react-hook-form + zod
 */
export function ReimbursementForm({ onClose, onSuccess }: ReimbursementFormProps) {
  // 1. Hook for Draft Persistence (Persistence Layer)
  const {
    data: draftData,
    setData: setDraftData,
    hasRestoredDraft,
    clearDraft,
    lastSavedAt,
  } = useEnhancedDraft<ReimbursementDraftData>({
    key: 'reimbursement_form',
    initialValue: initialDraftData,
    autoSaveDelay: 1000,
  })

  // 2. Form Initialization (Source of Truth)
  // Initialize from draftData if available
  const form = useForm<ReimbursementFormValues>({
    resolver: zodResolver(formSchema) as any, // Type assertion for strict optionality mismatch
    defaultValues: {
      type: draftData.type,
      amount: draftData.amount,
      currency: draftData.currency,
      description: draftData.description,
      uploadedFiles: draftData.uploadedFiles,
    },
    mode: 'onChange'
  })

  // 3. Sync Side-Effect
  // Watch all form changes and sync BACK to draft store
  useEffect(() => {
    const subscription = form.watch((value) => {
      setDraftData((prev) => ({
        ...prev,
        ...value as Partial<ReimbursementDraftData>
      }))
    })
    return () => subscription.unsubscribe()
  }, [form.watch, setDraftData])

  const createMutation = useCreateReimbursement()
  const uploadFileMutation = useUploadFile()

  // 上传图片到服务器
  const handleUpload = async (file: File): Promise<string> => {
    const result = await uploadFileMutation.mutateAsync({
      file,
      options: {
        category: 'receipt',
      },
    })

    // Manually update form state for files
    const currentFiles = form.getValues('uploadedFiles')
    form.setValue('uploadedFiles', [...currentFiles, result], { shouldDirty: true, shouldTouch: true })

    return `/api/v1/files/${result.id}/url`
  }

  const onSubmit: SubmitHandler<ReimbursementFormValues> = async (data) => {
    try {
      await createMutation.mutateAsync({
        type: data.type,
        amount: Math.round(data.amount * 100), // Convert to cents
        currency: data.currency,
        description: data.description,
      })
      clearDraft()
      onSuccess()
    } catch {
      // Error handled by mutation
    }
  }

  const isPending = createMutation.isPending

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
      <div className="w-full h-[90vh] sm:h-auto sm:max-h-[90vh] sm:max-w-md rounded-t-xl sm:rounded-lg bg-background shadow-lg flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3 sm:px-6 sm:py-4 shrink-0">
          <div>
            <h2 className="text-lg font-semibold">提交报销</h2>
            <DraftSavedIndicator lastSavedAt={lastSavedAt?.getTime() ?? null} />
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 hover:bg-muted active:bg-muted/80 -mr-2"
            aria-label="关闭"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Restore Prompt */}
        {hasRestoredDraft && (
          <div className="mx-4 mt-4 sm:mx-6 flex items-center justify-between rounded-md bg-blue-50 px-4 py-2 text-sm text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
            <span>已恢复上次编辑的草稿</span>
            <button
              type="button"
              onClick={() => {
                clearDraft()
                form.reset(initialDraftData)
              }}
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 underline"
            >
              清除草稿
            </button>
          </div>
        )}

        {/* Form Content */}
        <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} className="flex-1 overflow-y-auto">
          <div className="space-y-5 p-4 sm:p-6">
            {/* Type */}
            <div>
              <Label className="mb-2 block text-sm font-medium">报销类型</Label>
              <Controller
                control={form.control}
                name="type"
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger className="h-12 sm:h-10 text-base sm:text-sm">
                      <SelectValue placeholder="选择报销类型" />
                    </SelectTrigger>
                    <SelectContent>
                      {reimbursementTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value} className="py-3 sm:py-2">
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {form.formState.errors.type && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.type.message}</p>
              )}
            </div>

            {/* Amount */}
            <div>
              <Label className="mb-2 block text-sm font-medium">金额</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  placeholder="0.00"
                  className="flex-1 h-12 sm:h-10"
                  {...form.register('amount')}
                />
                <Controller
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <CurrencySelector
                      value={field.value}
                      onValueChange={field.onChange}
                    />
                  )}
                />
              </div>
              {form.formState.errors.amount && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.amount.message}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <Label className="mb-2 block text-sm font-medium">报销说明</Label>
              <Textarea
                placeholder="请详细描述报销事项"
                rows={4}
                className="resize-none"
                {...form.register('description')}
              />
              {form.formState.errors.description && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.description.message}</p>
              )}
            </div>

            {/* Upload */}
            <div>
              <Label className="mb-2 block text-sm font-medium">上传凭证</Label>
              <Controller
                control={form.control}
                name="uploadedFiles"
                render={({ field }) => (
                  <ImageUploader
                    value={field.value.map((f: FileMetadata) => `/api/v1/files/${f.id}/url`)}
                    onChange={(urls) => {
                      const current = field.value as FileMetadata[]
                      const remaining = current.filter((f) => urls.includes(`/api/v1/files/${f.id}/url`))
                      field.onChange(remaining)
                    }}
                    onUpload={handleUpload}
                    maxCount={5}
                    maxSizeMB={10}
                  />
                )}
              />
            </div>

            {/* Error */}
            {createMutation.error && (
              <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {createMutation.error instanceof Error ? createMutation.error.message : '提交失败'}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t px-4 py-4 sm:px-6 shrink-0 bg-background">
            <div className="flex gap-3 sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 sm:flex-none h-12 sm:h-10 rounded-md border px-6 text-base sm:text-sm font-medium hover:bg-muted active:bg-muted/80"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={isPending}
                className="flex-1 sm:flex-none h-12 sm:h-10 rounded-md bg-primary px-6 text-base sm:text-sm font-medium text-primary-foreground hover:bg-primary/90 active:bg-primary/80 disabled:opacity-50"
              >
                {isPending ? '提交中...' : '提交'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
